import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router';
import axios from 'axios';
import {
    Box, Paper, Typography, Button, TextField, CircularProgress,
    Alert, InputAdornment, IconButton,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import CloudIcon from '@mui/icons-material/Cloud';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

const API_URL = process.env.REACT_APP_BACKEND_API_URL || 'http://localhost:8080';

// Page publique (pas de session requise) : consomme le code d'invitation envoyé par email
// pour poser le premier mot de passe d'un compte créé sans mot de passe (voir EmployesPage.js,
// mode "invite"). Calquée sur SharePage.js pour rester cohérente sans dépendre de TokenContext.
export default function ActivateAccountPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const [email, setEmail] = useState(searchParams.get('email') || '');
    const [code, setCode] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPwd, setShowPwd] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (!email || !code || !password) {
            setError('Tous les champs sont requis.');
            return;
        }
        if (password !== confirmPassword) {
            setError('Les mots de passe ne correspondent pas.');
            return;
        }

        setSubmitting(true);
        try {
            await axios.post(`${API_URL}/api/admins/activate`, { email, code, password });
            setSuccess(true);
        } catch (e) {
            setError(e.response?.data?.error || 'Erreur lors de l\'activation du compte.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <CloudIcon color="primary" sx={{ fontSize: 32 }} />
                <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 'bold' }}>Activation de compte</Typography>
            </Box>

            <Paper variant="outlined" sx={{ maxWidth: 440, width: '100%', p: { xs: 2.5, sm: 4 }, borderRadius: 2 }}>
                {success ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <LockIcon sx={{ fontSize: 56, color: 'success.main' }} />
                        <Typography variant="h6" fontWeight="bold">Compte activé</Typography>
                        <Typography variant="body2" color="text.secondary" textAlign="center">
                            Votre mot de passe a été défini. Vous pouvez maintenant vous connecter.
                        </Typography>
                        <Button variant="contained" color="primary"
                            onClick={() => navigate('/')}>
                            Se connecter
                        </Button>
                    </Box>
                ) : (
                    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            Entrez le code d'activation reçu par email et choisissez votre mot de passe.
                        </Typography>

                        {error && <Alert severity="error">{error}</Alert>}

                        <TextField fullWidth label="Email" type="email" value={email}
                            onChange={(e) => setEmail(e.target.value)} required />
                        <TextField fullWidth label="Code d'activation" value={code}
                            onChange={(e) => setCode(e.target.value)} placeholder="XXX-XXX" required />
                        <TextField
                            fullWidth label="Nouveau mot de passe" type={showPwd ? 'text' : 'password'}
                            value={password} onChange={(e) => setPassword(e.target.value)} required
                            helperText="Au moins 10 caractères, avec majuscule, minuscule, chiffre et caractère spécial."
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowPwd(s => !s)} edge="end">
                                            {showPwd ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <TextField
                            fullWidth label="Confirmer le mot de passe" type={showPwd ? 'text' : 'password'}
                            value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required
                        />

                        <Button
                            type="submit" fullWidth variant="contained" color="primary" size="large"
                            disabled={submitting}
                            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : null}
                        >
                            {submitting ? 'Activation...' : 'Activer mon compte'}
                        </Button>
                    </Box>
                )}
            </Paper>
        </Box>
    );
}
